
import { defineComponent, inject } from 'vue'
import Dropdown from '@/components/shared/Dropdown.vue'
import TextInput from '@/components/shared/TextInput.vue'
import LoadingPulse from '@/components/shared/LoadingPulse.vue'
import Paginator from '@/components/shared/Paginator.vue'
import { PROVIDER_CONSTANTS } from '@/constants/provider.constants'
import Toaster from '@meforma/vue-toaster'
import TrashCanSvg from '@/components/svgs/TrashCanSvg.vue'
import { InventoryItem } from '@/@types/responses/inventory_item/InventoryItem'
import { InventoryTypeService } from '@/services/inventoryType.service'
import { InventoryItemService } from '@/services/inventoryItem.service'
import { InventoryTypeFieldValue } from '@/@types/responses/inventory_type/InventoryTypeFieldValue'
import Checkbox from '@/components/shared/Checkbox.vue'
import { InventoryTypeDropdownOption } from '@/@types/responses/inventory_type/InventoryTypeDropdownOption'
import { DropdownOptionResponse } from '@/@types/responses/common/DropdownOptionResponse'
import { InventoryTypeFieldOption } from '@/@types/responses/inventory_type/InventoryTypeFieldOption'
import { BuildStepService } from '@/services/buildStep.service'
import ManufacturerSearchMachine from '@/components/elements/ManufacturerSearchMachine.vue'
import SupplierSearchMachine from '@/components/elements/SupplierSearchMachine.vue'
import NumericInput from '@/components/shared/NumericInput.vue'
import { CaliberService } from '@/services/caliber.service'
import { InventoryTypeFieldSearchValueMessage } from '@/@types/messages/InventoryTypeFieldSearchValueMessage'
import { InventoryTypeSearchFieldModel } from '@/@types/models/InventoryTypeSearchFieldModel'
import { ErrorsResponse } from '@/@types/responses/common/ErrorsResponse'

export default defineComponent({
  name: 'InventorySearch',
  components: {
    Checkbox,
    Dropdown,
    LoadingPulse,
    ManufacturerSearchMachine,
    NumericInput,
    Paginator,
    SupplierSearchMachine,
    TextInput,
    TrashCanSvg
  },
  props: {
    showAdd: {
      type: Boolean,
      required: true
    },
    supplierId: {
      type: Number,
      default: undefined
    }
  },
  emits: ['inventory-item'],
  setup() {
    const buildStepService = inject(PROVIDER_CONSTANTS.buildStepService) as BuildStepService
    const caliberService = inject(PROVIDER_CONSTANTS.caliberService) as CaliberService
    const inventoryItemService = inject(PROVIDER_CONSTANTS.inventoryItemService) as InventoryItemService
    const inventoryTypeService = inject(PROVIDER_CONSTANTS.inventoryTypeService) as InventoryTypeService
    const toast = inject(PROVIDER_CONSTANTS.toaster) as Toaster

    return {
      buildStepService,
      caliberService,
      inventoryItemService,
      inventoryTypeService,
      toast
    }
  },
  data() {
    let inventoryTypeId: number|undefined

    const booleanOptions = [
      new DropdownOptionResponse('True', true, 'Yes'),
      new DropdownOptionResponse('False', true, 'No')
    ]

    return {
      ascending: false,
      booleanOptions,
      buildStepDropdownOptions: [] as DropdownOptionResponse<number>[],
      caliberDropdownOptions: [] as DropdownOptionResponse<number>[],
      coreProduct: this.showAdd,
      filterOptionsExpanded: true,
      inventoryItems: [] as InventoryItem[],
      inventoryTypeId,
      inventoryTypeDropdownOptions: [] as InventoryTypeDropdownOption[],
      inventoryTypeFieldSearchValues: [] as InventoryTypeFieldSearchValueMessage[],
      loading: false,
      pageNumber: 1,
      pageSize: 10,
      searchTerm: '',
      sortField: 'TotalQuantityOnHand',
      totalResults: 0
    }
  },
  computed: {
    fields(): string[] {
      if (this.inventoryItems.length === 0) return []

      return this.inventoryItems[0].inventoryTypeFieldValues.map(f => f.name)
    },
    searchFields(): InventoryTypeSearchFieldModel[] {
      if (!this.inventoryTypeId) return []

      const index = this.inventoryTypeDropdownOptions.findIndex(t => t.id === this.inventoryTypeId)
      if (index === -1) return []

      const inventoryTypeSearchFields = [] as InventoryTypeSearchFieldModel[]

      const inventoryTypeFields = this.inventoryTypeDropdownOptions[index].inventoryTypeFields
      for (let i = 0; i < inventoryTypeFields.length; i++) {
        const fieldValue = this.inventoryTypeFieldSearchValues.find(f => f.id === inventoryTypeFields[i].id)
        if (!fieldValue) continue

        inventoryTypeSearchFields.push(new InventoryTypeSearchFieldModel(inventoryTypeFields[i], fieldValue))
      }

      return inventoryTypeSearchFields
    }
  },
  watch: {
    inventoryTypeId(currentValue: number|undefined, oldValue: number|undefined): void {
      if (currentValue !== oldValue)
        this.buildInventoryTypeSearchFieldValues()
    }
  },
  mounted() {
    if (!this.showAdd)
      window.addEventListener("popstate", this.parseQueryStringAndSearch)

    Promise.all([
      this.fetchCaliberDropdownOptions(),
      this.fetchInventoryTypeDropdownOptions()
    ]).then(() => {
      this.parseQueryStringAndSearch()
    })
  },
  unmounted() {
    if (!this.showAdd)
      window.removeEventListener('popstate', this.parseQueryStringAndSearch)
  },
  methods: {
    boolToString(bool: boolean): string {
      return bool ? 'Yes' : 'No'
    },
    buildInventoryTypeSearchFieldValues(): void {
      if (!this.inventoryTypeId) return

      const index = this.inventoryTypeDropdownOptions.findIndex(t => t.id === this.inventoryTypeId)
      if (index === -1) return

      const inventoryTypeFieldSearchValueMessages = [] as InventoryTypeFieldSearchValueMessage[]

      const inventoryTypeFields = this.inventoryTypeDropdownOptions[index].inventoryTypeFields
      inventoryTypeFields.forEach(inventoryTypeField => {
        const inventoryTypeFieldValue = new InventoryTypeFieldSearchValueMessage(inventoryTypeField.id, '')
        inventoryTypeFieldSearchValueMessages.push(inventoryTypeFieldValue)
      })

      this.inventoryTypeFieldSearchValues = inventoryTypeFieldSearchValueMessages
    },
    clearSearchForm(): void {
      this.searchTerm = ''
      this.inventoryTypeId = undefined
      this.search()
    },
    fetchCaliberDropdownOptions(): Promise<void> {
      return this.caliberService.getCaliberDropdownOptions().then(dropdownOptionsResponse => {
        this.caliberDropdownOptions = dropdownOptionsResponse.dropdownOptions
      }).catch(error => {
        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to load the calibers. Try reloading the page.')
        }
      })
    },
    fetchInventoryTypeDropdownOptions(): Promise<void> {
      return this.inventoryTypeService.getInventoryTypeDropdownOptions(this.supplierId).then(dropdownOptionsResponse => {
        this.inventoryTypeDropdownOptions = dropdownOptionsResponse.dropdownOptions

        if (this.inventoryTypeDropdownOptions.length > 0)
          this.inventoryTypeId = this.inventoryTypeDropdownOptions[0].id
      }).catch(error => {
        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to load the inventory types. Please try reloading the page.')
        }
      })
    },
    fieldValueText(field: string, inventoryTypeFieldValues: InventoryTypeFieldValue[]): string {
      const index = inventoryTypeFieldValues.findIndex(i => i.name === field)
      if (index === -1) return ''

      return inventoryTypeFieldValues[index].displayValue
    },
    getBooleanParameterByName(name: string): boolean | undefined {
      const parameter = this.getStringParameterByName(name)
      return parameter !== undefined ? parameter === 'true' : undefined
    },
    getNumericParameterByName(name: string): number | undefined {
      const parameter = this.getStringParameterByName(name)
      return parameter !== undefined ? Number(parameter) : undefined
    },
    getStringParameterByName(name: string): string | undefined {
      const regex = new RegExp("[?&]" + name + "(=([^&#]*)|&|#|$)")
      const results = regex.exec(window.location.href)

      if (!results || !results[2])
        return undefined
      
      return decodeURIComponent(results[2].replace(/\+/g, " "));
    },
    onAddClicked(inventoryItem: InventoryItem): void {
      this.$emit('inventory-item', inventoryItem)
    },
    onDeleteClicked(id: number): void {
      this.inventoryItemService.deleteInventoryItem(id).then(() => {
        if ((this.inventoryItems.length - 1) === 0 && this.pageNumber > 1)
          this.onPageChanged(this.pageNumber - 1)
        else
          this.searchInventory()

        this.toast.success('Inventory item deleted successfully.')
      }).catch(error => {
        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to delete the inventory item. Please try again.')
        }
      })
    },
    onPageChanged(pageNumber: number): void {
      this.pageNumber = pageNumber
      this.searchInventory()
    },
    onPageSizeChanged(pageSize: number): void {
      this.pageSize = pageSize
      this.search()
    },
    onTableRowClicked(id: number): void {
      if (this.showAdd) {
        const routeData = this.$router.resolve('/inventory/' + id)
        window.open(routeData.href, '_blank')
      } else
        this.$router.push('/inventory/' + id)
    },
    optionsForDropdown(inventoryTypeFieldOptions: InventoryTypeFieldOption[]): DropdownOptionResponse<string>[] {
      const dropdownOptions = [] as DropdownOptionResponse<string>[]
      inventoryTypeFieldOptions.forEach(option => {
        dropdownOptions.push(new DropdownOptionResponse(option.id.toString(), option.active, option.value))
      })

      return dropdownOptions
    },
    parseQueryString(): void {
      const ascendingFromUrl = this.getBooleanParameterByName("a")
      this.ascending = ascendingFromUrl !== undefined ? ascendingFromUrl : this.ascending

      const coreProductFromUrl = this.getBooleanParameterByName("c")
      this.coreProduct = coreProductFromUrl !== undefined ? coreProductFromUrl : this.coreProduct

      const inventoryTypeIdFromUrl = this.getNumericParameterByName("i")
      this.inventoryTypeId = inventoryTypeIdFromUrl !== undefined ? inventoryTypeIdFromUrl : this.inventoryTypeId

      const pageNumberFromUrl = this.getNumericParameterByName("pn")
      this.pageNumber = pageNumberFromUrl !== undefined ? pageNumberFromUrl : this.pageNumber

      const pageSizeFromUrl = this.getNumericParameterByName("ps")
      this.pageSize = pageSizeFromUrl !== undefined ? pageSizeFromUrl : this.pageSize

      const searchTermFromUrl = this.getStringParameterByName("st")
      this.searchTerm = searchTermFromUrl !== undefined ? searchTermFromUrl : this.searchTerm

      const sortFieldFromUrl = this.getStringParameterByName("sf")
      this.sortField = sortFieldFromUrl !== undefined ? sortFieldFromUrl : this.sortField

      const fieldsFromUrl = this.getStringParameterByName("f")
      if (fieldsFromUrl !== undefined) {
        const fields = fieldsFromUrl.split(',')

        fields.forEach(field => {
          const values = field.split(':')
          
          const inventoryTypeFieldSearchValue = this.inventoryTypeFieldSearchValues.find(i => i.id === parseInt(values[0]))
          if (inventoryTypeFieldSearchValue !== undefined)
            inventoryTypeFieldSearchValue.value = values[1]
        })
      }
    },
    parseQueryStringAndSearch(): void {
      if (!this.showAdd)
        this.parseQueryString()

      this.searchInventory()
    },
    search(): void {
      this.pageNumber = 1
      this.searchInventory()
    },
    searchInventory(): void {
      if (!this.inventoryTypeId) return

      this.loading = true

      if (!this.showAdd) this.updateUrl()

      this.inventoryTypeFieldSearchValues.forEach(element => {
        element.value = String(element.value)
      });

      this.inventoryItemService.searchInventoryItems(this.pageNumber, this.pageSize, this.sortField, this.ascending, this.inventoryTypeId, this.searchTerm, this.coreProduct, this.inventoryTypeFieldSearchValues).then(pagingResponse => {
        this.totalResults = pagingResponse.totalResults
        this.inventoryItems = pagingResponse.results
        this.loading = false
      }).catch(error => {
        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to load the inventory items. Please try reloading the page.')
        }
      })
    },
    sortBy(sortField: string): void {
      if (this.sortField === sortField)
        this.ascending = !this.ascending
      else
        this.sortField = sortField
      
      this.searchInventory()
    },
    updateUrl(): void {
      let newQueryString = `?a=${this.ascending}&pn=${this.pageNumber}&ps=${this.pageSize}&sf=${this.sortField}`

      if (this.coreProduct)
        newQueryString = newQueryString + '&c=true'

      if (this.inventoryTypeId !== undefined)
        newQueryString = newQueryString + `&i=${this.inventoryTypeId}`

      if (this.searchTerm !== '')
        newQueryString = newQueryString + `&st=${encodeURIComponent(this.searchTerm)}`

      if (this.inventoryTypeFieldSearchValues.length > 0) {
        const fields = [] as string[]

        this.inventoryTypeFieldSearchValues.forEach(inventoryTypeFieldSearchValue => {
          if (inventoryTypeFieldSearchValue.value !== '')
            fields.push(`${inventoryTypeFieldSearchValue.id}:${inventoryTypeFieldSearchValue.value}`)
        })

        if (fields.length > 0)
          newQueryString = newQueryString + `&f=${encodeURIComponent(fields.join(','))}`
      }
      
      this.$router.replace('/inventory' + newQueryString)
    }
  }
})
